import React, { useEffect, useMemo, useRef, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { FileUpload } from 'primereact/fileupload';
import { Password } from 'primereact/password';
import { Chips } from 'primereact/chips';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { InputCepCustom, InputCnpjCpfCustom, InputTelefoneCustom, formatCnpjCpf, formatTelefone } from '../lanz-ui';
import api, { normalize } from '../services/api';
import { consultarCnpj, camposDaConsultaCnpj, resumoRegimeConsultaCnpj } from '../services/cnpj';
import { downloadModeloFiliaisExcel, parseFiliaisExcelFile } from '../services/filiaisExcel';
import { parseCnaesList, cnaesToJson } from '../utils/cnaeHelpers';
import {
  AMBIENTE_FISCAL_OPCOES,
  AMBIENTE_FISCAL_PADRAO,
  REGIME_TRIBUTARIO_OPCOES,
  syncOptanteComRegime,
  syncRegimeComOptante,
} from '../utils/fiscalCadastro';
import CadastroDataTable from '../components/CadastroDataTable/CadastroDataTable';
import './EmpresaPage.css';
import './FiliaisPage.css';
import './CertificadoPage.css';
import './AgentesPage.css';

const STATUS_OPCOES = [
  { label: 'Ativo', value: 'Ativo' },
  { label: 'Inativo', value: 'Inativo' },
];

const DIAS_ALERTA_VENCIMENTO = 7;
const LABEL_EMPRESA_PRINCIPAL = 'Empresa principal';

const EMPTY_EMPRESA = {
  razaosocial: '',
  fantasia: '',
  cnpjcpf: '',
  email: '',
  telefone: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  uf: '',
  cep: '',
  codigoIbge: '',
  inscricaoMunicipal: '',
  cnaePrincipal: '',
  cnaesSecundarios: [],
  regimeTributario: '',
  optanteSimples: false,
  serieDpsPadrao: '',
  ambienteFiscal: AMBIENTE_FISCAL_PADRAO,
  downloadDestinadas: false,
};

const EMPTY_FILIAL = {
  id: null,
  razaosocial: '',
  fantasia: '',
  pessoa: 'Juridica',
  cnpjcpf: '',
  email: '',
  telefone: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  uf: '',
  cep: '',
  codigoIbge: '',
  inscricaoMunicipal: '',
  cnaePrincipal: '',
  cnaesSecundarios: [],
  regimeTributario: '',
  optanteSimples: false,
  ambienteFiscal: AMBIENTE_FISCAL_PADRAO,
  downloadDestinadas: true,
  status: 'Ativo',
};

function EmpresaPage({ embedded = false }) {
  const toast = useRef(null);
  const fileUploadRef = useRef(null);

  const [form, setForm] = useState(EMPTY_EMPRESA);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [resolvendoIbge, setResolvendoIbge] = useState(false);
  const [logo, setLogo] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [filiais, setFiliais] = useState([]);
  const [loadingFiliais, setLoadingFiliais] = useState(true);
  const [filialDialogVisible, setFilialDialogVisible] = useState(false);
  const [filialForm, setFilialForm] = useState(EMPTY_FILIAL);
  const [salvandoFilial, setSalvandoFilial] = useState(false);

  const [certificados, setCertificados] = useState([]);
  const [loadingCerts, setLoadingCerts] = useState(false);
  const [certDialogVisible, setCertDialogVisible] = useState(false);
  const [certContexto, setCertContexto] = useState({ filialId: null, label: LABEL_EMPRESA_PRINCIPAL });
  const [enviandoCert, setEnviandoCert] = useState(false);
  const [arquivo, setArquivo] = useState(null);
  const [senha, setSenha] = useState('');
  const [usoEmissao, setUsoEmissao] = useState(true);
  const [usoDestinadas, setUsoDestinadas] = useState(false);
  const [consultandoCnpj, setConsultandoCnpj] = useState(false);
  const [consultandoCnpjFilial, setConsultandoCnpjFilial] = useState(false);
  const [consultandoCnaeEmpresa, setConsultandoCnaeEmpresa] = useState(false);
  const [consultandoCnaeFilial, setConsultandoCnaeFilial] = useState(false);
  const [importPreviewFiliais, setImportPreviewFiliais] = useState(null);
  const [importandoFiliais, setImportandoFiliais] = useState(false);
  const filialFileInputRef = useRef(null);

  useEffect(() => {
    carregarEmpresa();
    carregarFiliais();
    carregarCertificados();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const carregarEmpresa = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/empresas/me');
      const empresa = normalize(data);
      setForm({
        razaosocial: empresa.razaosocial || '',
        fantasia: empresa.fantasia || '',
        cnpjcpf: formatCnpjCpf(empresa.cnpjcpf || '', 'cnpj'),
        email: empresa.email || '',
        telefone: formatTelefone(empresa.telefone || ''),
        logradouro: empresa.logradouro || '',
        numero: empresa.numero || '',
        complemento: empresa.complemento || '',
        bairro: empresa.bairro || '',
        cidade: empresa.cidade || '',
        uf: empresa.uf || '',
        cep: empresa.cep || '',
        codigoIbge: empresa.codigoIbge || '',
        inscricaoMunicipal: empresa.inscricaoMunicipal || '',
        cnaePrincipal: empresa.cnaePrincipal || '',
        cnaesSecundarios: parseCnaesList(empresa.cnaesSecundariosJson),
        regimeTributario: empresa.regimeTributario || '',
        optanteSimples: !!empresa.optanteSimples,
        serieDpsPadrao: empresa.serieDpsPadrao || '',
        ambienteFiscal: empresa.ambienteFiscal || AMBIENTE_FISCAL_PADRAO,
        downloadDestinadas: !!empresa.downloadDestinadas,
      });
      setLogo(empresa.imagepath || '');
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Erro', detail: 'Não foi possível carregar os dados da empresa.' });
    } finally {
      setLoading(false);
    }
  };

  const carregarFiliais = async () => {
    setLoadingFiliais(true);
    try {
      const { data } = await api.get('/filiais');
      setFiliais(normalize(data));
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Erro', detail: 'Não foi possível carregar as filiais.' });
    } finally {
      setLoadingFiliais(false);
    }
  };

  const carregarCertificados = async () => {
    setLoadingCerts(true);
    try {
      const { data } = await api.get('/empresacertificados');
      setCertificados(normalize(data));
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Erro', detail: 'Não foi possível carregar os certificados.' });
    } finally {
      setLoadingCerts(false);
    }
  };

  const setCampo = (campo, valor) => setForm((prev) => ({ ...prev, [campo]: valor }));
  const setFilialCampo = (campo, valor) => setFilialForm((prev) => ({ ...prev, [campo]: valor }));

  const aplicarConsultaCnpj = (alvo, dados) => {
    const secundarios = parseCnaesList(dados.cnaesSecundariosJson);
    const patch = {
      ...dados,
      cnaesSecundarios: secundarios,
    };
    delete patch.cnaesSecundariosJson;
    if (alvo === 'empresa') {
      setForm((prev) => ({
        ...prev,
        ...patch,
        // Não apagar CNAE já cadastrado se a RFB vier vazia
        cnaePrincipal: dados.cnaePrincipal || prev.cnaePrincipal,
        cnaesSecundarios: secundarios.length ? secundarios : prev.cnaesSecundarios,
        telefone: formatTelefone(dados.telefone || prev.telefone),
        cep: dados.cep || prev.cep,
        ...(dados.regimeTributario ? { regimeTributario: dados.regimeTributario } : {}),
        ...(typeof dados.optanteSimples === 'boolean' ? { optanteSimples: dados.optanteSimples } : {}),
      }));
    } else {
      setFilialForm((prev) => ({
        ...prev,
        ...patch,
        cnaePrincipal: dados.cnaePrincipal || prev.cnaePrincipal,
        cnaesSecundarios: secundarios.length ? secundarios : prev.cnaesSecundarios,
        telefone: formatTelefone(dados.telefone || prev.telefone),
        cep: dados.cep || prev.cep,
        ...(dados.regimeTributario ? { regimeTributario: dados.regimeTributario } : {}),
        ...(typeof dados.optanteSimples === 'boolean' ? { optanteSimples: dados.optanteSimples } : {}),
      }));
    }
  };

  const consultarSomenteCnae = async (alvo) => {
    const cnpj = alvo === 'empresa' ? form.cnpjcpf : filialForm.cnpjcpf;
    const setLoadingCnae = alvo === 'empresa' ? setConsultandoCnaeEmpresa : setConsultandoCnaeFilial;
    setLoadingCnae(true);
    try {
      const empresa = await consultarCnpj(cnpj);
      if (!empresa || empresa.status !== 'OK') {
        toast.current?.show({
          severity: 'warn',
          summary: 'CNAE',
          detail: empresa?.message || 'Não foi possível consultar o CNAE deste CNPJ.',
        });
        return;
      }
      const principal = empresa.cnaePrincipal || '';
      const secundarios = parseCnaesList(empresa.cnaesSecundarios);
      if (alvo === 'empresa') {
        setForm((prev) => ({
          ...prev,
          cnaePrincipal: principal || prev.cnaePrincipal,
          cnaesSecundarios: secundarios.length ? secundarios : prev.cnaesSecundarios,
          ...(empresa.regimeTributario ? { regimeTributario: empresa.regimeTributario } : {}),
          ...(typeof empresa.optanteSimples === 'boolean' ? { optanteSimples: empresa.optanteSimples } : {}),
        }));
      } else {
        setFilialForm((prev) => ({
          ...prev,
          cnaePrincipal: principal || prev.cnaePrincipal,
          cnaesSecundarios: secundarios.length ? secundarios : prev.cnaesSecundarios,
        }));
      }
      const regimeTxt = resumoRegimeConsultaCnpj(empresa);
      toast.current?.show({
        severity: 'success',
        summary: 'CNAE atualizado',
        detail: [
          principal ? `Principal ${principal}${secundarios.length ? ` · ${secundarios.length} secundário(s)` : ''}` : null,
          regimeTxt,
        ].filter(Boolean).join(' · ') || 'Consulta OK, mas a RFB não retornou CNAE.',
      });
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'CNAE',
        detail: error.code === 'INVALID_CNPJ' ? error.message : 'Erro ao consultar CNAE. Tente novamente.',
      });
    } finally {
      setLoadingCnae(false);
    }
  };

  const consultarCnpjEmpresa = async () => {
    setConsultandoCnpj(true);
    try {
      const empresa = await consultarCnpj(form.cnpjcpf);
      const campos = camposDaConsultaCnpj(empresa);
      if (!campos) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Consulta CNPJ',
          detail: empresa?.message || 'Não foi possível encontrar os dados do CNPJ.',
        });
        return;
      }
      aplicarConsultaCnpj('empresa', campos);
      const ibge = await resolverIbgeCodigo(campos.cidade, campos.uf);
      if (ibge) setCampo('codigoIbge', ibge);
      const cnaeTxt = campos.cnaePrincipal
        ? `CNAE ${campos.cnaePrincipal}`
        : null;
      const regimeTxt = resumoRegimeConsultaCnpj(empresa);
      const partes = ['Dados carregados', ibge ? `IBGE: ${ibge}` : null, cnaeTxt, regimeTxt].filter(Boolean);
      toast.current?.show({
        severity: 'success',
        summary: 'Consulta CNPJ',
        detail: partes.join(' · '),
      });
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Consulta CNPJ',
        detail: error.code === 'INVALID_CNPJ' ? error.message : 'Erro ao consultar CNPJ. Tente novamente.',
      });
    } finally {
      setConsultandoCnpj(false);
    }
  };

  const consultarCnpjFilial = async () => {
    setConsultandoCnpjFilial(true);
    try {
      const empresa = await consultarCnpj(filialForm.cnpjcpf);
      const campos = camposDaConsultaCnpj(empresa);
      if (!campos) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Consulta CNPJ',
          detail: empresa?.message || 'Não foi possível encontrar os dados do CNPJ.',
        });
        return;
      }
      aplicarConsultaCnpj('filial', campos);
      const ibge = await resolverIbgeCodigo(campos.cidade, campos.uf);
      if (ibge) setFilialCampo('codigoIbge', ibge);
      const cnaeTxt = campos.cnaePrincipal
        ? `CNAE ${campos.cnaePrincipal}`
        : null;
      const partes = ['Dados carregados', ibge ? `IBGE: ${ibge}` : null, cnaeTxt].filter(Boolean);
      toast.current?.show({
        severity: 'success',
        summary: 'Consulta CNPJ',
        detail: partes.join(' · '),
      });
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Consulta CNPJ',
        detail: error.code === 'INVALID_CNPJ' ? error.message : 'Erro ao consultar CNPJ. Tente novamente.',
      });
    } finally {
      setConsultandoCnpjFilial(false);
    }
  };

  const salvarEmpresa = async () => {
    setSalvando(true);
    try {
      await api.put('/empresas/me', {
        razaosocial: form.razaosocial,
        fantasia: form.fantasia,
        cnpjcpf: form.cnpjcpf,
        email: form.email,
        telefone: form.telefone,
        logradouro: form.logradouro,
        numero: form.numero,
        complemento: form.complemento,
        bairro: form.bairro,
        cidade: form.cidade,
        uf: form.uf,
        cep: form.cep,
        codigoIbge: form.codigoIbge,
        inscricaoMunicipal: form.inscricaoMunicipal,
        cnaePrincipal: form.cnaePrincipal,
        cnaesSecundariosJson: cnaesToJson(form.cnaesSecundarios),
        regimeTributario: form.regimeTributario || null,
        optanteSimples: !!form.optanteSimples,
        serieDpsPadrao: form.serieDpsPadrao,
        ambienteFiscal: form.ambienteFiscal,
        downloadDestinadas: form.downloadDestinadas,
      });
      toast.current?.show({ severity: 'success', summary: 'Sucesso', detail: 'Dados da empresa atualizados.' });
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Erro',
        detail: error.response?.data?.message || 'Não foi possível salvar os dados da empresa.',
      });
    } finally {
      setSalvando(false);
    }
  };

  const uploadLogo = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const body = new FormData();
      body.append('arquivo', file);
      const { data } = await api.post('/empresas/me/logo', body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const empresa = normalize(data);
      setLogo(empresa.imagepath || '');
      toast.current?.show({ severity: 'success', summary: 'Logo atualizado', detail: 'Logotipo salvo com sucesso.' });
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Erro',
        detail: error.response?.data?.message || 'Não foi possível enviar o logo.',
      });
    } finally {
      setUploadingLogo(false);
      event.target.value = '';
    }
  };

  const removerLogo = async () => {
    setUploadingLogo(true);
    try {
      await api.delete('/empresas/me/logo');
      setLogo('');
      toast.current?.show({ severity: 'success', summary: 'Logo removido' });
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Erro', detail: 'Não foi possível remover o logo.' });
    } finally {
      setUploadingLogo(false);
    }
  };

  const resolverIbgeCodigo = async (cidade, uf) => {
    if (!cidade || !uf) return null;
    try {
      const { data } = await api.get('/util/ibge', { params: { cidade, uf } });
      return normalize(data)?.codigoIbge || null;
    } catch {
      return null;
    }
  };

  const resolverIbge = async () => {
    if (!form.cidade || !form.uf) {
      toast.current?.show({ severity: 'warn', summary: 'Atenção', detail: 'Informe cidade e UF antes de resolver o código IBGE.' });
      return;
    }
    setResolvendoIbge(true);
    try {
      const codigo = await resolverIbgeCodigo(form.cidade, form.uf);
      if (!codigo) {
        toast.current?.show({ severity: 'error', summary: 'Erro', detail: 'Município não encontrado.' });
        return;
      }
      setCampo('codigoIbge', codigo);
      toast.current?.show({ severity: 'success', summary: 'IBGE resolvido', detail: `Código: ${codigo}` });
    } finally {
      setResolvendoIbge(false);
    }
  };

  const abrirNovaFilial = () => {
    setFilialForm(EMPTY_FILIAL);
    setFilialDialogVisible(true);
  };

  const abrirEdicaoFilial = (filial) => {
    setFilialForm({
      ...EMPTY_FILIAL,
      ...filial,
      cnpjcpf: formatCnpjCpf(filial.cnpjcpf || '', 'cnpj'),
      telefone: formatTelefone(filial.telefone || ''),
      cnaesSecundarios: parseCnaesList(filial.cnaesSecundariosJson || filial.cnaesSecundarios),
      regimeTributario: filial.regimeTributario || '',
      optanteSimples: !!filial.optanteSimples,
      ambienteFiscal: filial.ambienteFiscal || AMBIENTE_FISCAL_PADRAO,
    });
    setFilialDialogVisible(true);
  };

  const salvarFilial = async () => {
    setSalvandoFilial(true);
    try {
      const payload = {
        razaosocial: filialForm.razaosocial,
        fantasia: filialForm.fantasia,
        pessoa: filialForm.pessoa,
        cnpjcpf: filialForm.cnpjcpf,
        email: filialForm.email,
        telefone: filialForm.telefone,
        logradouro: filialForm.logradouro,
        numero: filialForm.numero,
        complemento: filialForm.complemento,
        bairro: filialForm.bairro,
        cidade: filialForm.cidade,
        uf: filialForm.uf,
        cep: filialForm.cep,
        codigoIbge: filialForm.codigoIbge,
        inscricaoMunicipal: filialForm.inscricaoMunicipal,
        cnaePrincipal: filialForm.cnaePrincipal,
        cnaesSecundariosJson: cnaesToJson(filialForm.cnaesSecundarios),
        regimeTributario: filialForm.regimeTributario || null,
        optanteSimples: !!filialForm.optanteSimples,
        ambienteFiscal: filialForm.ambienteFiscal || AMBIENTE_FISCAL_PADRAO,
        downloadDestinadas: filialForm.downloadDestinadas,
      };

      if (filialForm.id) {
        await api.put(`/filiais/${filialForm.id}`, { ...payload, status: filialForm.status });
      } else {
        await api.post('/filiais', payload);
      }

      toast.current?.show({ severity: 'success', summary: 'Sucesso', detail: 'Filial salva com sucesso.' });
      setFilialDialogVisible(false);
      carregarFiliais();
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Erro',
        detail: error.response?.data?.message || 'Não foi possível salvar a filial.',
      });
    } finally {
      setSalvandoFilial(false);
    }
  };

  const excluirFilial = (filial) => {
    confirmDialog({
      message: `Remover a filial "${filial.fantasia || filial.razaosocial}"?`,
      header: 'Confirmação',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await api.delete(`/filiais/${filial.id}`);
          toast.current?.show({ severity: 'success', summary: 'Removida', detail: 'Filial removida com sucesso.' });
          carregarFiliais();
          carregarCertificados();
        } catch (error) {
          toast.current?.show({ severity: 'error', summary: 'Erro', detail: 'Não foi possível remover a filial.' });
        }
      },
    });
  };

  const limparUploadCert = () => {
    setArquivo(null);
    setSenha('');
    setUsoEmissao(true);
    setUsoDestinadas(false);
    fileUploadRef.current?.clear();
  };

  const abrirCertificados = (filialId, label) => {
    setCertContexto({ filialId: filialId ?? null, label: label || LABEL_EMPRESA_PRINCIPAL });
    limparUploadCert();
    setCertDialogVisible(true);
  };

  const certsDoContexto = useMemo(() => {
    return certificados.filter((c) => {
      if (certContexto.filialId == null) return !c.filialId;
      return c.filialId === certContexto.filialId;
    });
  }, [certificados, certContexto.filialId]);

  const enviarCertificado = async () => {
    if (!arquivo) {
      toast.current?.show({ severity: 'warn', summary: 'Atenção', detail: 'Selecione o arquivo .pfx do certificado.' });
      return;
    }
    if (!senha) {
      toast.current?.show({ severity: 'warn', summary: 'Atenção', detail: 'Informe a senha do certificado.' });
      return;
    }

    const formData = new FormData();
    formData.append('Arquivo', arquivo);
    formData.append('Senha', senha);
    if (certContexto.filialId) formData.append('FilialId', certContexto.filialId);
    formData.append('UsoEmissao', usoEmissao);
    formData.append('UsoDestinadas', usoDestinadas);

    setEnviandoCert(true);
    try {
      await api.post('/empresacertificados/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.current?.show({ severity: 'success', summary: 'Sucesso', detail: 'Certificado enviado com sucesso.' });
      limparUploadCert();
      carregarCertificados();
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Erro',
        detail: error.response?.data?.message || 'Não foi possível enviar o certificado.',
      });
    } finally {
      setEnviandoCert(false);
    }
  };

  const excluirCertificado = (certificado) => {
    confirmDialog({
      message: `Remover o certificado de "${certificado.cnpjTitular}"?`,
      header: 'Confirmação',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await api.delete(`/empresacertificados/${certificado.id}`);
          toast.current?.show({ severity: 'success', summary: 'Removido', detail: 'Certificado removido.' });
          carregarCertificados();
        } catch (error) {
          toast.current?.show({ severity: 'error', summary: 'Erro', detail: 'Não foi possível remover o certificado.' });
        }
      },
    });
  };

  const resumoCertMatriz = useMemo(
    () => certificados.filter((c) => !c.filialId),
    [certificados]
  );

  const contagemCertsFilial = (filialId) =>
    certificados.filter((c) => c.filialId === filialId).length;

  const onEscolherExcelFiliais = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const linhas = await parseFiliaisExcelFile(file);
      const { data } = await api.post('/filiais/importar', { somenteValidar: true, linhas });
      setImportPreviewFiliais({ resultado: normalize(data), linhas });
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Erro',
        detail: error.response?.data?.message || 'Falha ao ler/validar o Excel.',
      });
    } finally {
      event.target.value = '';
    }
  };

  const confirmarImportacaoFiliais = async () => {
    if (!importPreviewFiliais?.linhas?.length) return;
    setImportandoFiliais(true);
    try {
      const { data } = await api.post('/filiais/importar', {
        somenteValidar: false,
        linhas: importPreviewFiliais.linhas,
      });
      const r = normalize(data);
      toast.current?.show({
        severity: 'success',
        summary: 'Importação',
        detail: `${r.importadas || 0} filial(is) importada(s).`,
      });
      setImportPreviewFiliais(null);
      carregarFiliais();
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Erro', detail: 'Falha ao importar filiais.' });
    } finally {
      setImportandoFiliais(false);
    }
  };

  const colunasFiliais = useMemo(
    () => [
      { field: 'razaosocial', header: 'Razão social' },
      { field: 'fantasia', header: 'Fantasia' },
      { field: 'cnpjcpf', header: 'CNPJ' },
      { field: 'email', header: 'E-mail', defaultVisible: false },
      { field: 'telefone', header: 'Telefone', defaultVisible: false },
      { field: 'logradouro', header: 'Logradouro', defaultVisible: false },
      { field: 'numero', header: 'Número', defaultVisible: false },
      { field: 'bairro', header: 'Bairro', defaultVisible: false },
      { field: 'cidade', header: 'Cidade' },
      { field: 'uf', header: 'UF', style: { width: 70 } },
      { field: 'cep', header: 'CEP', defaultVisible: false },
      { field: 'inscricaoMunicipal', header: 'Inscrição municipal', defaultVisible: false },
      { field: 'cnaePrincipal', header: 'CNAE', defaultVisible: false },
      {
        field: 'status',
        header: 'Status',
        style: { width: 110 },
        body: (row) => <Tag severity={row.status === 'Ativo' ? 'success' : 'warning'} value={row.status} />,
        exportValue: (row) => row.status,
      },
      {
        field: '_certs',
        header: 'Certificados',
        style: { width: 120 },
        body: (row) => contagemCertsFilial(row.id),
        exportValue: (row) => contagemCertsFilial(row.id),
      },
      {
        field: '_acoes',
        header: 'Ações',
        sortable: false,
        alwaysVisible: true,
        style: { width: 160 },
        body: (row) => (
          <div className="filiais-acoes">
            <Button icon="pi pi-pencil" rounded text onClick={() => abrirEdicaoFilial(row)} tooltip="Editar filial" />
            <Button
              icon="pi pi-id-card"
              rounded
              text
              onClick={() => abrirCertificados(row.id, row.fantasia || row.razaosocial)}
              tooltip="Certificados"
            />
            <Button icon="pi pi-trash" rounded text severity="danger" onClick={() => excluirFilial(row)} tooltip="Excluir" />
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [certificados]
  );

  if (loading) {
    return (
      <div className="empresa-page">
        <p>Carregando...</p>
      </div>
    );
  }

  return (
    <div className={`empresa-page${embedded ? ' empresa-page--embedded' : ''}`}>
      <Toast ref={toast} />
      <ConfirmDialog acceptLabel="Sim" rejectLabel="Não" />

      {!embedded && <h1>Empresa</h1>}

      <section className="empresa-section">
        <div className="empresa-section-header">
          <h2>Dados da empresa principal</h2>
        </div>

        <div className="empresa-logo-box">
          <div className="empresa-logo-preview">
            {logo ? <img src={logo} alt="Logo da empresa" /> : <span>Sem logo</span>}
          </div>
          <div className="empresa-logo-actions">
            <label className="empresa-logo-upload">
              <input type="file" accept="image/*" onChange={uploadLogo} disabled={uploadingLogo} />
              {uploadingLogo ? 'Enviando...' : 'Enviar logo'}
            </label>
            {logo && (
              <Button label="Remover logo" className="p-button-text p-button-danger" onClick={removerLogo} disabled={uploadingLogo} />
            )}
            <Button
              label="Certificados"
              icon="pi pi-id-card"
              className="p-button-outlined"
              onClick={() => abrirCertificados(null, form.fantasia || form.razaosocial || LABEL_EMPRESA_PRINCIPAL)}
            />
            {resumoCertMatriz.length > 0 && (
              <span className="empresa-cert-resumo">
                {resumoCertMatriz.length} certificado(s) na empresa principal
              </span>
            )}
          </div>
        </div>

        <div className="empresa-form">
          <div className="nfse-form-row">
            <div className="empresa-field empresa-field-cnpj">
              <label htmlFor="empresa-cnpj">CNPJ</label>
              <div className="empresa-ibge-row">
                <InputCnpjCpfCustom
                  id="empresa-cnpj"
                  modo="cnpj"
                  value={form.cnpjcpf}
                  onChange={(value) => setCampo('cnpjcpf', value)}
                />
                <Button
                  label="Consultar"
                  icon="pi pi-search"
                  className="p-button-outlined"
                  loading={consultandoCnpj}
                  onClick={consultarCnpjEmpresa}
                />
              </div>
            </div>
          </div>

          <div className="nfse-form-row">
            <div className="empresa-field">
              <label htmlFor="empresa-razaosocial">Razão social</label>
              <InputText id="empresa-razaosocial" value={form.razaosocial} onChange={(e) => setCampo('razaosocial', e.target.value)} />
            </div>
            <div className="empresa-field">
              <label htmlFor="empresa-fantasia">Nome fantasia</label>
              <InputText id="empresa-fantasia" value={form.fantasia} onChange={(e) => setCampo('fantasia', e.target.value)} />
            </div>
          </div>

          <div className="nfse-form-row">
            <div className="empresa-field">
              <label htmlFor="empresa-email">E-mail</label>
              <InputText id="empresa-email" value={form.email} onChange={(e) => setCampo('email', e.target.value)} />
            </div>
            <div className="empresa-field">
              <label htmlFor="empresa-telefone">Telefone</label>
              <InputTelefoneCustom
                id="empresa-telefone"
                value={form.telefone}
                onChange={(value) => setCampo('telefone', value)}
              />
            </div>
            <div className="empresa-field empresa-field-sm">
              <label>CEP</label>
              <InputCepCustom
                value={form.cep}
                onChange={(value) => setCampo('cep', value)}
                onCepDataFetch={(data) => {
                  if (data && !data.erro) {
                    setForm((prev) => ({
                      ...prev,
                      logradouro: data.logradouro || prev.logradouro,
                      bairro: data.bairro || prev.bairro,
                      cidade: data.localidade || prev.cidade,
                      uf: data.uf || prev.uf,
                    }));
                  }
                }}
              />
            </div>
          </div>

          <div className="nfse-form-row">
            <div className="empresa-field">
              <label htmlFor="empresa-logradouro">Logradouro</label>
              <InputText id="empresa-logradouro" value={form.logradouro} onChange={(e) => setCampo('logradouro', e.target.value)} />
            </div>
            <div className="empresa-field empresa-field-sm">
              <label htmlFor="empresa-numero">Número</label>
              <InputText id="empresa-numero" value={form.numero} onChange={(e) => setCampo('numero', e.target.value)} />
            </div>
            <div className="empresa-field">
              <label htmlFor="empresa-complemento">Complemento</label>
              <InputText id="empresa-complemento" value={form.complemento} onChange={(e) => setCampo('complemento', e.target.value)} />
            </div>
          </div>

          <div className="nfse-form-row">
            <div className="empresa-field">
              <label htmlFor="empresa-bairro">Bairro</label>
              <InputText id="empresa-bairro" value={form.bairro} onChange={(e) => setCampo('bairro', e.target.value)} />
            </div>
            <div className="empresa-field">
              <label htmlFor="empresa-cidade">Cidade</label>
              <InputText id="empresa-cidade" value={form.cidade} onChange={(e) => setCampo('cidade', e.target.value)} />
            </div>
            <div className="empresa-field empresa-field-sm">
              <label htmlFor="empresa-uf">UF</label>
              <InputText id="empresa-uf" value={form.uf} maxLength={2} onChange={(e) => setCampo('uf', e.target.value.toUpperCase())} />
            </div>
          </div>

          <div className="nfse-form-row">
            <div className="empresa-field">
              <label htmlFor="empresa-ibge">Código IBGE</label>
              <div className="empresa-ibge-row">
                <InputText id="empresa-ibge" value={form.codigoIbge} readOnly />
                <Button
                  label="Resolver IBGE"
                  icon="pi pi-map-marker"
                  className="p-button-outlined"
                  loading={resolvendoIbge}
                  onClick={resolverIbge}
                />
              </div>
            </div>
            <div className="empresa-field">
              <label htmlFor="empresa-im">Inscrição Municipal</label>
              <InputText id="empresa-im" value={form.inscricaoMunicipal} onChange={(e) => setCampo('inscricaoMunicipal', e.target.value)} />
            </div>
          </div>

          <div className="nfse-form-row">
            <div className="empresa-field empresa-field-cnae">
              <label htmlFor="empresa-cnae">CNAE Principal</label>
              <div className="empresa-cnae-row">
                <InputText
                  id="empresa-cnae"
                  value={form.cnaePrincipal}
                  onChange={(e) => setCampo('cnaePrincipal', e.target.value)}
                  placeholder="Ex.: 6201501"
                />
                <Button
                  label="Consultar CNAE"
                  icon="pi pi-search"
                  className="p-button-outlined"
                  loading={consultandoCnaeEmpresa}
                  onClick={() => consultarSomenteCnae('empresa')}
                  tooltip="Busca CNAE principal e secundários na RFB pelo CNPJ cadastrado"
                  tooltipOptions={{ position: 'top' }}
                />
              </div>
            </div>
          </div>

          <div className="nfse-form-row">
            <div className="empresa-field">
              <label htmlFor="empresa-regime">Regime tributário</label>
              <Dropdown
                id="empresa-regime"
                value={form.regimeTributario || ''}
                options={REGIME_TRIBUTARIO_OPCOES}
                onChange={(e) => {
                  const regime = e.value || '';
                  setForm((prev) => ({
                    ...prev,
                    regimeTributario: regime,
                    optanteSimples: syncOptanteComRegime(regime, prev.optanteSimples),
                  }));
                }}
                placeholder="Selecione"
                className="w-full"
                showClear
              />
              <small style={{ color: '#64748b' }}>
                Usado na DPS (opSimpNac). Preenchido automaticamente na Consulta CNPJ.
              </small>
            </div>
            <div className="empresa-field empresa-field-checkbox">
              <Checkbox
                inputId="empresa-optanteSimples"
                checked={!!form.optanteSimples}
                onChange={(e) => {
                  const checked = !!e.checked;
                  setForm((prev) => ({
                    ...prev,
                    optanteSimples: checked,
                    regimeTributario: syncRegimeComOptante(checked, prev.regimeTributario),
                  }));
                }}
              />
              <label htmlFor="empresa-optanteSimples">Optante pelo Simples Nacional / MEI</label>
            </div>
          </div>

          <div className="nfse-form-row">
            <div className="empresa-field empresa-field-sm">
              <label htmlFor="empresa-serie">Série DPS</label>
              <InputText id="empresa-serie" value={form.serieDpsPadrao} onChange={(e) => setCampo('serieDpsPadrao', e.target.value)} />
            </div>
            <div className="empresa-field">
              <label htmlFor="empresa-ambiente">Ambiente fiscal</label>
              <Dropdown
                id="empresa-ambiente"
                value={form.ambienteFiscal}
                options={AMBIENTE_FISCAL_OPCOES}
                onChange={(e) => setCampo('ambienteFiscal', e.value)}
                className="w-full"
              />
            </div>
          </div>

          <div className="nfse-form-row">
            <div className="empresa-field empresa-field-full">
              <label htmlFor="empresa-cnaes-sec">CNAEs secundários</label>
              <Chips
                id="empresa-cnaes-sec"
                value={form.cnaesSecundarios || []}
                onChange={(e) => setCampo('cnaesSecundarios', parseCnaesList(e.value))}
                separator=","
                allowDuplicate={false}
                className="w-full"
                placeholder="Digite o código e Enter (vários permitidos)"
              />
              <small style={{ color: '#64748b' }}>
                Vários CNAEs são armazenados e aparecem na emissão como opções de item de serviço.
                Use &quot;Consultar CNAE&quot; para preencher pela RFB.
              </small>
            </div>
          </div>

          <div className="nfse-form-row">
            <div className="empresa-field empresa-field-checkbox">
              <Checkbox
                inputId="empresa-downloadDestinadas"
                checked={form.downloadDestinadas}
                onChange={(e) => setCampo('downloadDestinadas', e.checked)}
              />
              <label htmlFor="empresa-downloadDestinadas">Baixar NFS-e destinadas automaticamente</label>
            </div>
          </div>

          <div className="empresa-actions">
            <Button label="Salvar dados da empresa" icon="pi pi-save" loading={salvando} onClick={salvarEmpresa} />
          </div>
        </div>
      </section>

      <section className="empresa-section">
        <div className="empresa-section-header">
          <h2>Filiais ou CNPJs adicionais</h2>
          <div className="agentes-header-actions">
            <Button
              label="Modelo Excel"
              icon="pi pi-download"
              className="p-button-outlined"
              onClick={downloadModeloFiliaisExcel}
            />
            <Button
              label="Importar Excel"
              icon="pi pi-upload"
              className="p-button-outlined"
              onClick={() => filialFileInputRef.current?.click()}
            />
            <input
              ref={filialFileInputRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={onEscolherExcelFiliais}
            />
            <Button label="Adicionar empresa" icon="pi pi-plus" onClick={abrirNovaFilial} />
          </div>
        </div>

        {importPreviewFiliais && (
          <div className="agentes-import-preview">
            <strong>Validação prévia:</strong>{' '}
            {importPreviewFiliais.resultado?.validas || 0} válida(s),{' '}
            {importPreviewFiliais.resultado?.invalidas || 0} inválida(s)
            {importPreviewFiliais.resultado?.erros?.length > 0 && (
              <ul>
                {importPreviewFiliais.resultado.erros.slice(0, 10).map((e) => (
                  <li key={`${e.linha}-${e.mensagem}`}>
                    Linha {e.linha}: {e.mensagem}
                  </li>
                ))}
              </ul>
            )}
            <div className="agentes-import-actions">
              <Button label="Cancelar" className="p-button-text" onClick={() => setImportPreviewFiliais(null)} />
              <Button
                label="Confirmar importação das válidas"
                icon="pi pi-check"
                loading={importandoFiliais}
                disabled={!importPreviewFiliais.resultado?.validas}
                onClick={confirmarImportacaoFiliais}
              />
            </div>
          </div>
        )}

        <CadastroDataTable
          value={filiais}
          columns={colunasFiliais}
          storageKey="filiais"
          loading={loadingFiliais}
          title="Filiais"
          emptyMessage="Nenhuma filial cadastrada."
          globalFilterFields={['razaosocial', 'fantasia', 'cnpjcpf', 'cidade', 'uf', 'status']}
        />
      </section>

      {/* Diálogo filial */}
      <Dialog
        header={filialForm.id ? 'Editar empresa' : 'Adicionar empresa'}
        visible={filialDialogVisible}
        onHide={() => setFilialDialogVisible(false)}
        style={{ width: '900px' }}
        breakpoints={{ '960px': '95vw' }}
      >
        <div className="filiais-form">
          <div className="nfse-form-row">
            <div className="filiais-field filiais-field-cnpj">
              <label htmlFor="filial-cnpj">CNPJ</label>
              <div className="empresa-ibge-row">
                <InputCnpjCpfCustom
                  id="filial-cnpj"
                  modo="cnpj"
                  value={filialForm.cnpjcpf}
                  onChange={(value) => setFilialCampo('cnpjcpf', value)}
                />
                <Button
                  label="Consultar"
                  icon="pi pi-search"
                  className="p-button-outlined"
                  loading={consultandoCnpjFilial}
                  onClick={consultarCnpjFilial}
                />
              </div>
            </div>
          </div>

          <div className="nfse-form-row">
            <div className="filiais-field">
              <label htmlFor="filial-razaosocial">Razão social</label>
              <InputText id="filial-razaosocial" value={filialForm.razaosocial} onChange={(e) => setFilialCampo('razaosocial', e.target.value)} />
            </div>
            <div className="filiais-field">
              <label htmlFor="filial-fantasia">Nome fantasia</label>
              <InputText id="filial-fantasia" value={filialForm.fantasia} onChange={(e) => setFilialCampo('fantasia', e.target.value)} />
            </div>
          </div>

          <div className="nfse-form-row">
            <div className="filiais-field">
              <label htmlFor="filial-email">E-mail</label>
              <InputText id="filial-email" value={filialForm.email} onChange={(e) => setFilialCampo('email', e.target.value)} />
            </div>
            <div className="filiais-field">
              <label htmlFor="filial-telefone">Telefone</label>
              <InputTelefoneCustom
                id="filial-telefone"
                value={filialForm.telefone}
                onChange={(value) => setFilialCampo('telefone', value)}
              />
            </div>
            <div className="filiais-field filiais-field-sm">
              <label>CEP</label>
              <InputCepCustom
                value={filialForm.cep}
                onChange={(value) => setFilialCampo('cep', value)}
                onCepDataFetch={(data) => {
                  if (data && !data.erro) {
                    setFilialForm((prev) => ({
                      ...prev,
                      logradouro: data.logradouro || prev.logradouro,
                      bairro: data.bairro || prev.bairro,
                      cidade: data.localidade || prev.cidade,
                      uf: data.uf || prev.uf,
                    }));
                  }
                }}
              />
            </div>
          </div>

          <div className="nfse-form-row">
            <div className="filiais-field">
              <label htmlFor="filial-logradouro">Logradouro</label>
              <InputText id="filial-logradouro" value={filialForm.logradouro} onChange={(e) => setFilialCampo('logradouro', e.target.value)} />
            </div>
            <div className="filiais-field filiais-field-sm">
              <label htmlFor="filial-numero">Número</label>
              <InputText id="filial-numero" value={filialForm.numero} onChange={(e) => setFilialCampo('numero', e.target.value)} />
            </div>
            <div className="filiais-field">
              <label htmlFor="filial-complemento">Complemento</label>
              <InputText id="filial-complemento" value={filialForm.complemento} onChange={(e) => setFilialCampo('complemento', e.target.value)} />
            </div>
          </div>

          <div className="nfse-form-row">
            <div className="filiais-field">
              <label htmlFor="filial-bairro">Bairro</label>
              <InputText id="filial-bairro" value={filialForm.bairro} onChange={(e) => setFilialCampo('bairro', e.target.value)} />
            </div>
            <div className="filiais-field">
              <label htmlFor="filial-cidade">Cidade</label>
              <InputText id="filial-cidade" value={filialForm.cidade} onChange={(e) => setFilialCampo('cidade', e.target.value)} />
            </div>
            <div className="filiais-field filiais-field-sm">
              <label htmlFor="filial-uf">UF</label>
              <InputText id="filial-uf" value={filialForm.uf} maxLength={2} onChange={(e) => setFilialCampo('uf', e.target.value.toUpperCase())} />
            </div>
          </div>

          <div className="nfse-form-row">
            <div className="filiais-field">
              <label htmlFor="filial-ibge">Código IBGE</label>
              <InputText id="filial-ibge" value={filialForm.codigoIbge} onChange={(e) => setFilialCampo('codigoIbge', e.target.value)} />
            </div>
            <div className="filiais-field">
              <label htmlFor="filial-im">Inscrição Municipal</label>
              <InputText id="filial-im" value={filialForm.inscricaoMunicipal} onChange={(e) => setFilialCampo('inscricaoMunicipal', e.target.value)} />
            </div>
            {filialForm.id && (
              <div className="filiais-field">
                <label>Status</label>
                <Dropdown value={filialForm.status} options={STATUS_OPCOES} onChange={(e) => setFilialCampo('status', e.value)} />
              </div>
            )}
          </div>

          <div className="nfse-form-row">
            <div className="filiais-field filiais-field-cnae">
              <label htmlFor="filial-cnae">CNAE Principal</label>
              <div className="empresa-cnae-row">
                <InputText
                  id="filial-cnae"
                  value={filialForm.cnaePrincipal}
                  onChange={(e) => setFilialCampo('cnaePrincipal', e.target.value)}
                  placeholder="Ex.: 6201501"
                />
                <Button
                  label="Consultar CNAE"
                  icon="pi pi-search"
                  className="p-button-outlined"
                  loading={consultandoCnaeFilial}
                  onClick={() => consultarSomenteCnae('filial')}
                />
              </div>
            </div>
          </div>

          <div className="nfse-form-row">
            <div className="filiais-field filiais-field-full">
              <label htmlFor="filial-cnaes-sec">CNAEs secundários</label>
              <Chips
                id="filial-cnaes-sec"
                value={filialForm.cnaesSecundarios || []}
                onChange={(e) => setFilialCampo('cnaesSecundarios', parseCnaesList(e.value))}
                separator=","
                allowDuplicate={false}
                className="w-full"
                placeholder="Digite o código e Enter (vários permitidos)"
              />
            </div>
          </div>

          <div className="nfse-form-row">
            <div className="filiais-field">
              <label htmlFor="filial-regime">Regime tributário</label>
              <Dropdown
                id="filial-regime"
                value={filialForm.regimeTributario || ''}
                options={REGIME_TRIBUTARIO_OPCOES}
                onChange={(e) => {
                  const regime = e.value || '';
                  setFilialForm((prev) => ({
                    ...prev,
                    regimeTributario: regime,
                    optanteSimples: syncOptanteComRegime(regime, prev.optanteSimples),
                  }));
                }}
                placeholder="Selecione"
                className="w-full"
                showClear
              />
            </div>
            <div className="filiais-field filiais-field-checkbox">
              <Checkbox
                inputId="filial-optanteSimples"
                checked={!!filialForm.optanteSimples}
                onChange={(e) => {
                  const checked = !!e.checked;
                  setFilialForm((prev) => ({
                    ...prev,
                    optanteSimples: checked,
                    regimeTributario: syncRegimeComOptante(checked, prev.regimeTributario),
                  }));
                }}
              />
              <label htmlFor="filial-optanteSimples">Optante Simples / MEI</label>
            </div>
          </div>

          <div className="nfse-form-row">
            <div className="filiais-field">
              <label htmlFor="filial-ambiente">Ambiente fiscal</label>
              <Dropdown
                id="filial-ambiente"
                value={filialForm.ambienteFiscal || AMBIENTE_FISCAL_PADRAO}
                options={AMBIENTE_FISCAL_OPCOES}
                onChange={(e) => setFilialCampo('ambienteFiscal', e.value)}
                className="w-full"
              />
            </div>
          </div>

          <div className="nfse-form-row">
            <div className="filiais-field filiais-field-checkbox">
              <Checkbox
                inputId="filial-downloadDestinadas"
                checked={filialForm.downloadDestinadas}
                onChange={(e) => setFilialCampo('downloadDestinadas', e.checked)}
              />
              <label htmlFor="filial-downloadDestinadas">Baixar NFS-e destinadas automaticamente</label>
            </div>
          </div>
        </div>

        <div className="filiais-dialog-footer">
          <Button label="Cancelar" className="p-button-text" onClick={() => setFilialDialogVisible(false)} />
          <Button label="Salvar" icon="pi pi-save" loading={salvandoFilial} onClick={salvarFilial} />
        </div>
      </Dialog>

      {/* Diálogo certificados (matriz ou filial já selecionada) */}
      <Dialog
        header={`Certificados — ${certContexto.label}`}
        visible={certDialogVisible}
        onHide={() => setCertDialogVisible(false)}
        style={{ width: '860px' }}
        breakpoints={{ '960px': '95vw' }}
      >
        <div className="certificado-upload-card" style={{ marginBottom: '1rem', boxShadow: 'none', padding: '8px 4px 16px' }}>
          <h2>Enviar certificado (.pfx)</h2>
          <p className="empresa-cert-contexto">
            Vínculo: <strong>{certContexto.label}</strong>
          </p>
          <div className="certificado-upload-form">
            <div className="nfse-form-row">
              <div className="certificado-field">
                <label>Arquivo</label>
                <FileUpload
                  ref={fileUploadRef}
                  mode="basic"
                  chooseLabel="Selecionar arquivo .pfx"
                  accept=".pfx,.p12"
                  auto={false}
                  customUpload
                  uploadHandler={() => {}}
                  onSelect={(e) => setArquivo(e.files?.[0] || null)}
                  onClear={() => setArquivo(null)}
                />
              </div>
              <div className="certificado-field">
                <label>Senha</label>
                <Password
                  placeholder="Senha do certificado"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  feedback={false}
                  toggleMask
                />
              </div>
            </div>

            <div className="nfse-form-row">
              <div className="certificado-field certificado-field-full">
                <div className="certificado-checkboxes">
                  <div className="certificado-checkbox">
                    <Checkbox inputId="cert-usoEmissao" checked={usoEmissao} onChange={(e) => setUsoEmissao(e.checked)} />
                    <label htmlFor="cert-usoEmissao">Uso para emissão</label>
                  </div>
                  <div className="certificado-checkbox">
                    <Checkbox inputId="cert-usoDestinadas" checked={usoDestinadas} onChange={(e) => setUsoDestinadas(e.checked)} />
                    <label htmlFor="cert-usoDestinadas">Uso para download de destinadas</label>
                  </div>
                </div>
              </div>
            </div>

            <div className="certificado-actions">
              <Button label="Enviar certificado" icon="pi pi-upload" loading={enviandoCert} onClick={enviarCertificado} />
            </div>
          </div>
        </div>

        <DataTable
          value={certsDoContexto}
          loading={loadingCerts}
          emptyMessage="Nenhum certificado neste vínculo."
        >
          <Column
            header="Ações"
            body={(row) => (
              <Button icon="pi pi-trash" rounded text severity="danger" onClick={() => excluirCertificado(row)} tooltip="Excluir" />
            )}
          />
          <Column field="cnpjTitular" header="CNPJ titular" />
          <Column
            header="Validade"
            body={(row) => (
              <span>
                {row.validadeInicio ? new Date(row.validadeInicio).toLocaleDateString('pt-BR') : '-'}
                {' até '}
                {row.validadeFim ? new Date(row.validadeFim).toLocaleDateString('pt-BR') : '-'}
              </span>
            )}
          />
          <Column
            header="Situação"
            body={(row) =>
              row.diasParaVencer <= DIAS_ALERTA_VENCIMENTO ? (
                <Tag severity="danger" value={`Vence em ${row.diasParaVencer} dia(s)`} />
              ) : (
                <Tag severity="success" value={`Válido (${row.diasParaVencer} dias)`} />
              )
            }
          />
          <Column header="Emissão" body={(row) => (row.usoEmissao ? 'Sim' : 'Não')} />
          <Column header="Destinadas" body={(row) => (row.usoDestinadas ? 'Sim' : 'Não')} />
        </DataTable>
      </Dialog>
    </div>
  );
}

export default EmpresaPage;
